import { AudioLoader, AudioListener, Audio } from "three";

export class AudioManager {
  constructor() {
    this.audioListener = new AudioListener();
    this.audioLoader = new AudioLoader();
    this.currentTrack = null;
    this.currentTrackName = null;
    this.currentBuffer = null; // Keep track of current buffer for proper disposal
    this.loadingTrack = false; // Flag to prevent multiple simultaneous loads
    this.tracks = [
      "ElectricWonderland.mp3",
      "LoomingPinnacle.mp3",
      "LostInTheAbyss.mp3",
      "NeonPulse.mp3",
      "RidingTheWaves.mp3",
      "SadEnding.mp3",
      "SpaceVoyage.mp3",
      "SuperBlockBoyTheme.mp3",
      "WhispersOfTheUnknown.mp3",
      "Ascension.mp3",
      "CelestialShimmer.mp3",
      "DanceOfDemons.mp3",
      "DigitalShowdown.mp3",
      "EchoesOfMyself.mp3",
      "MysteriousReverie.mp3",
      "MeanderingRoam.mp3",
    ];
    this.isPlaying = false;

    // Load muted state from localStorage, default to false if not set
    this.isMuted = localStorage.getItem("musicMuted") === "true";
  }

  formatTrackName(filename) {
    // Remove .mp3 extension and split into words
    const words = filename.replace(".mp3", "").split(/(?=[A-Z])/);

    // Words to keep lowercase
    const lowercaseWords = ["of", "the", "in", "and", "to", "a", "an"];

    // Format each word
    return words
      .map((word) => {
        const lowerWord = word.toLowerCase();
        return lowercaseWords.includes(lowerWord) ? lowerWord : word;
      })
      .join(" ");
  }

  getCurrentTrackName() {
    return this.currentTrackName;
  }

  init(camera) {
    camera.add(this.audioListener);
  }

  cleanupCurrentTrack() {
    if (this.currentTrack) {
      // Remove event listener to prevent callbacks after disposal
      this.currentTrack.onEnded = null;

      // Stop the sound if it's playing
      if (this.currentTrack.isPlaying) {
        this.currentTrack.stop();
      }

      // Disconnect from audio nodes
      this.currentTrack.disconnect();

      // Remove the buffer reference from the Audio object
      this.currentTrack.setBuffer(null);

      this.currentTrack = null;
    }

    // Clear the buffer reference
    if (this.currentBuffer) {
      this.currentBuffer = null;
    }

    this.isPlaying = false;
  }

  playRandomTrack() {
    // Don't start a new track if music is muted or a track is already playing or loading
    if (this.isMuted || this.isPlaying || this.loadingTrack) {
      return;
    }

    this.loadingTrack = true;

    // First, ensure any existing track is properly cleaned up
    this.cleanupCurrentTrack();

    const randomIndex = Math.floor(Math.random() * this.tracks.length);

    // Get the base path from the deployed location (if any)
    const basePath = import.meta.env.BASE_URL || "/";
    const trackPath = `${basePath}music/${this.tracks[randomIndex]}`;

    this.currentTrackName = this.formatTrackName(this.tracks[randomIndex]);

    try {
      this.audioLoader.load(
        trackPath,
        (buffer) => {
          // Check if music was muted during loading
          if (this.isMuted) {
            this.loadingTrack = false;
            return;
          }

          // Create a new Audio instance
          this.currentTrack = new Audio(this.audioListener);

          // Store buffer reference for later cleanup
          this.currentBuffer = buffer;

          this.currentTrack.setBuffer(buffer);
          this.currentTrack.setLoop(false);

          // Register onEnded BEFORE calling play
          this.currentTrack.onEnded = () => {
            this.handleTrackEnded();
          };

          // Start the track
          this.currentTrack.play();
          this.isPlaying = true;
          this.loadingTrack = false;
        },
        // Progress callback
        (progress) => {
          // Optional: track loading progress
        },
        // Error callback
        (error) => {
          console.error("Error loading audio:", error);
          this.loadingTrack = false;
          // Try another track after a short delay
          setTimeout(() => this.playRandomTrack(), 1000);
        }
      );
    } catch (error) {
      console.error("Exception during audio loading:", error);
      this.loadingTrack = false;
      // Try another track after a short delay
      setTimeout(() => this.playRandomTrack(), 1000);
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;

    // Save muted state to localStorage
    localStorage.setItem("musicMuted", this.isMuted);

    if (this.isMuted) {
      // When muting, stop and clean up the music entirely
      this.cleanupCurrentTrack();
    } else {
      // When unmuting, start a new random track
      this.playRandomTrack();
    }

    return this.isMuted;
  }

  skipTrack() {
    // If we're already loading a track, don't try to load another one
    if (this.loadingTrack) {
      return;
    }

    // Clean up the current track properly
    this.cleanupCurrentTrack();

    // Start a new random track
    this.playRandomTrack();
  }

  stop() {
    this.cleanupCurrentTrack();
  }

  pause() {
    if (this.currentTrack && this.isPlaying) {
      this.currentTrack.pause();
      this.isPlaying = false;
    }
  }

  resume() {
    if (this.currentTrack && !this.isPlaying) {
      this.currentTrack.play();
      this.isPlaying = true;
    }
  }

  handleTrackEnded() {
    // Clean up the track that just ended
    this.cleanupCurrentTrack();

    // Start a new track with a slight delay to prevent audio context issues
    setTimeout(() => this.playRandomTrack(), 1000);
  }

  // Method to properly dispose when the game is unloaded
  dispose() {
    this.cleanupCurrentTrack();

    // Remove listener from camera if needed
    if (this.audioListener && this.audioListener.parent) {
      this.audioListener.parent.remove(this.audioListener);
    }

    // Clear all references
    this.audioListener = null;
    this.audioLoader = null;
    this.tracks = null;
    this.currentTrackName = null;
  }
}
